import dayjs from 'dayjs';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { isCornMaturity, resolveExpectedHarvestDate } from './corn-stage';
import type { StageMessage } from './shared';

/**
 * Past-harvest-date banner — corn only, advisor-facing.
 *
 * Shown to advisor-type users when an active corn crop monitoring is at maturity and the
 * estimated harvest date has passed, but harvest has not been registered yet. Prompts either
 * closing the campaign without data or registering harvest to unlock the campaign report.
 *
 * Returns `null` for non-corn/non-advisor users, before the estimated harvest date, or once
 * harvest is registered.
 */
export const usePastHarvestDateStageMessage = ({
    cropMonitoring,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isValidatedBayerUserType = useIsValidatedBayerUserType();

    if (!isValidatedBayerUserType || !isCornMaturity(cropMonitoring, { withoutHarvest: true })) return null;

    const expectedHarvestDate = resolveExpectedHarvestDate(cropMonitoring?.cornCropMonitoring);
    if (!expectedHarvestDate?.isValid() || !dayjs().isAfter(expectedHarvestDate)) return null;

    return {
        title: _('Ya pasó la fecha estimada de cosecha'),
        message: _(
            'El cultivo alcanzó la humedad necesaria para cosechar. Si el productor ya cosechó, registrá la cosecha para acceder al reporte de campaña con los resultados del lote.'
        ),
        secondaryAction: {
            label: _('Finalizar campaña sin registrar datos'),
            action: 'finalize-campaign-without-data',
        },
        primaryAction: { label: _('Obtener reporte'), action: 'harvest-report' },
    };
};
